import React, { useState, useEffect } from 'react';
import { useAltHotkeys } from '../hooks/useAltHotkeys';
import { createPortal } from 'react-dom';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useModalDrag } from '../hooks/useModalDrag';
import ModalHeader from './ui/ModalHeader';
import dispatchRemovalService, {
  DispatchRemoveCategory,
} from '../services/dispatchRemoval.service';

export interface ReasonNoteResult {
  category_id: number;
  category_name: string;
  reason_id: number | null;
  reason_name: string | null;
  note: string;
}

export interface ReasonNoteModalProps {
  onClose: () => void;
  onSubmit: (result: ReasonNoteResult) => void;
}

export function ReasonNoteModal({ onClose, onSubmit }: ReasonNoteModalProps) {
  const [note, setNote] = useState('');
  const [categories, setCategories] = useState<DispatchRemoveCategory[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | ''>('');
  const [selectedReasonId, setSelectedReasonId] = useState<number | ''>('');
  const [loadingCategories, setLoadingCategories] = useState(true);

  useEffect(() => {
    dispatchRemovalService.getCategories(false).then(data => {
      setCategories(data);
      setLoadingCategories(false);
    }).catch(() => {
      setLoadingCategories(false);
    });
  }, []);

  const selectedCategory = categories.find(c => c.id === selectedCategoryId) || null;
  const showReasonDropdown = selectedCategory?.has_subcategories && selectedCategory.reasons.length > 0;

  const isValid =
    selectedCategoryId !== '' &&
    note.trim() !== '' &&
    (!showReasonDropdown || selectedReasonId !== '');

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    setSelectedCategoryId(val === '' ? '' : Number(val));
    setSelectedReasonId('');
  };

  const handleSubmit = () => {
    if (!isValid) return;
    const selectedReason = showReasonDropdown
      ? selectedCategory?.reasons.find(r => r.id === selectedReasonId) ?? null
      : null;
    onSubmit({
      category_id: selectedCategoryId as number,
      category_name: selectedCategory!.name,
      reason_id: showReasonDropdown ? (selectedReasonId as number) : null,
      reason_name: selectedReason?.name ?? null,
      note: note.trim(),
    });
  };

  // Rendered only while open. Alt+S submits the note (a real persist), Alt+C closes.
  useAltHotkeys({ S: handleSubmit, C: onClose }, undefined, { layer: true });

  // Unmounted by the parent when closed, so `true` is fine — unmount resets state.
  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(true);

  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-scrim/50">
      <div
        ref={panelRef}
        style={panelStyle}
        className="bg-panel rounded-lg shadow-xl max-w-md w-full mx-4"
      >
        <ModalHeader
          title={
            <span className="flex items-center gap-2">
              <ExclamationTriangleIcon className="w-5 h-5 text-amber-400" />
              Reason Required
            </span>
          }
          onClose={onClose}
          dragHandleProps={dragHandleProps}
        />

        {/* Content */}
        <div className="px-6 py-4 space-y-4">
          <p className="text-label">
            One or more of the selected orders are past a deadline. Please provide a reason for removing the driver.
          </p>

          {/* Category dropdown */}
          <div>
            <label className="block text-sm font-medium text-label mb-1">Category</label>
            <select
              value={selectedCategoryId}
              onChange={handleCategoryChange}
              disabled={loadingCategories}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-body focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            >
              <option value="">— Select a category —</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>

          {/* Reason dropdown (conditional) */}
          {showReasonDropdown && (
            <div>
              <label className="block text-sm font-medium text-label mb-1">Reason</label>
              <select
                value={selectedReasonId}
                onChange={e => setSelectedReasonId(e.target.value === '' ? '' : Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-body focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">— Select a reason —</option>
                {selectedCategory!.reasons.map(r => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-label mb-1">Notes</label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-body placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              rows={3}
              placeholder="Enter notes..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-label bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!isValid}
          >
            Submit
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default ReasonNoteModal;
